
import Vue from 'vue'
import Component from 'vue-class-component'
import {Watch} from 'vue-property-decorator'
import {State} from 'vuex-class'
import TempAccountService from '@/services/TempAccountService'
import TempAccount from '@/models/tempAccount'
import GridHeader from '@/models/gridHeader'
import Permissions from '../models/constants/Permissions'

import CardTable from '../components/Layout/CardTable.vue'
import TempAccountCreate from '@/components/TempAccounts/TempAccountCreate.vue'
import TempAccountUpdate from '@/components/TempAccounts/TempAccountUpdate.vue'
import TempAccountChangeStatus from '@/components/TempAccounts/TempAccountChangeStatus.vue'
import TempAccountDelete from '@/components/TempAccounts/TempAccountDelete.vue'
import ResetSinglePassword from '../components/Users/ResetSinglePassword.vue'
import CardTitle from '../components/Atomic/CardTitle.vue'
import BlueButton from '../components/Atomic/BlueButton.vue'
import RedButton from '../components/Atomic/RedButton.vue'
import HelpTooltip from '../components/Atomic/HelpTooltip.vue'
import UserMassInsert from '@/views/UserMassInsert.vue'

import ErrorHandler from '@/services/ErrorHandlerService'
import {INotificationService} from '@/services/NotificationService'
import SearchField from '@/components/Atomic/SearchField.vue'
import DatePickerActions from '@/components/Atomic/DatePickerActions.vue'
import {User} from '../store/index'
import ToolTipButton from '@/components/Atomic/ToolTipButton.vue'

@Component({
  components: {
    ToolTipButton,
    CardTable,
    DatePickerActions,
    TempAccountCreate,
    TempAccountUpdate,
    TempAccountChangeStatus,
    TempAccountDelete,
    ResetSinglePassword,
    UserMassInsert,
    CardTitle,
    SearchField,
    BlueButton,
    RedButton,
    HelpTooltip,
  },
})
export default class TempAccounts extends Vue {
  public $refs!: {
    createDialog: TempAccountCreate,
    updateDialog: TempAccountUpdate,
    accountChangeStatusDialog: TempAccountChangeStatus,
    deleteDialog: TempAccountDelete,
    datePickerDialog: DatePickerActions,
  }
  @State private user!: User
  private isPending: boolean = false
  private tempAccounts: TempAccount[] = []
  private filteredAccounts: TempAccount[] = []
  private headers: GridHeader[] = []
  private filter: string = ''
  private descriptions: string[] = []

  private names: string[] = []
  private firstNames: string[] = []
  private usernames: string[] = []
  private descriptionsFilter: string[] = []
  private emails: string[] = []
  private expirationDates: string[] = []
  private accountStatus: string[] = ['Activé', 'Désactivé']

  private menu: boolean = false
  private selectedName: string = ''
  private selectedFirstName: string = ''
  private selectedUsername: string = ''
  private selectedExpiration: any = ''
  private selectedDescription: string = ''
  private selectedEmail: string = ''
  private selectedAccountStatus: string = ''

  private $notificationService!: INotificationService
  private errorHandler = new ErrorHandler()

  constructor() {
    super()
  }

  private get hasAccessToManageTempUser(): boolean {
    const allowed = [Permissions.ManageTempAccounts]
    if (
        (this.user.permissions as []).some((permission) =>
            allowed.includes(permission),
        )
    ) {
      return true
    }
    return false
  }

  private get hasUsersToFilter() {
    return this.tempAccounts.length > 0
  }

  private async resetPassword(user: TempAccount) {
    this.isPending = true
    const username = user.identifiant

    try {
      const response = await TempAccountService.resetUserPassword(user)
      const message =
          this.$t('USER.ResetPasswordSucceedNotStudent').toString() +
          username +
          this.$t('USER.ResetPasswordSucceedNotStudent2').toString() +
          response['email']
      this.$notificationService.success(message)
    } catch (error: any) {
      this.errorHandler.handle(this, error.response.data.code, true)
    } finally {
      this.isPending = false
    }
  }

  private async search(filter: string) {
    this.isPending = true
    try {
      this.tempAccounts = await TempAccountService.search(filter)
    } catch (error: any) {
      if (error.response !== undefined) {
        this.errorHandler.handle(this, error.response.data.code, true)
      }
    } finally {
      this.tempAccounts.forEach((user) => {
        this.populateFilterData(user)
      })
      this.isPending = false
      this.filterLogical()
    }
  }

  private async fetchDescriptions() {
    this.isPending = true
    try {
      this.descriptions = await TempAccountService.fetchDescriptions()
    } catch (error: any) {
      if (error.response !== undefined) {
        this.descriptions = []
      }
    } finally {
      this.isPending = false
    }
  }

  private populateFilterData(user: TempAccount) {
    this.populateNames(user)
    this.populateFirstNames(user)
    this.populateUsernames(user)
    this.populateEmails(user)
    this.populateDescriptions(user)
    this.populateExpirationDates(user)
    this.sortFilterData()
  }

  private sortFilterData() {
    this.names.sort()
    this.firstNames.sort()
    this.usernames.sort()
    this.descriptionsFilter.sort()
    this.emails.sort()
  }

  private populateNames(user: TempAccount) {
    if (user.nom) {
      if (!this.names.includes(user.nom)) {
        this.names.push(user.nom)
      }
    }
  }

  private populateFirstNames(user: TempAccount) {
    if (user.prenom) {
      if (!this.firstNames.includes(user.prenom)) {
        this.firstNames.push(user.prenom)
      }
    }
  }

  private populateUsernames(user: TempAccount) {
    if (user.identifiant) {
      if (!this.usernames.includes(user.identifiant)) {
        this.usernames.push(user.identifiant)
      }
    }
  }

  private populateDescriptions(user: TempAccount) {
    if (user.description) {
      if (!this.descriptionsFilter.includes(user.description)) {
        this.descriptionsFilter.push(user.description)
      }
    }
  }

  private populateEmails(user: TempAccount) {
    if (user.courriel_autre) {
      if (!this.emails.includes(user.courriel_autre)) {
        this.emails.push(user.courriel_autre)
      }
    }
  }

  private populateExpirationDates(user: TempAccount) {
    const expirationDate = user.date_validite.toString()
    if (!this.expirationDates.includes(expirationDate)) {
      this.expirationDates.push(expirationDate)
    }
  }

  private allowedDates(val: string) {
    return this.expirationDates.indexOf(val) !== -1
  }

  private resetFilterDialog() {
    this.clearFilterData()
    this.clearFiltersSelection()
    this.filterLogical()
  }

  private clearFilterData() {
    this.names = []
    this.firstNames = []
    this.usernames = []
    this.descriptionsFilter = []
    this.emails = []
    this.expirationDates = []
  }

  private clearFiltersSelection() {
    this.selectedName = ''
    this.selectedFirstName = ''
    this.selectedUsername = ''
    this.selectedExpiration = ''
    this.selectedDescription = ''
    this.selectedEmail = ''
    this.selectedAccountStatus = ''
  }

  private mounted() {
    this.fetchDescriptions()
    this.createHeaders()
  }

  private createHeaders() {
    this.headers.push(
        new GridHeader(
            this.$t('Username').toString(),
            'identifiant',
            'left',
            true,
        ),
    )
    this.headers.push(
        new GridHeader(this.$t('Name').toString(), 'nom', 'left', true),
    )
    this.headers.push(
        new GridHeader(this.$t('FirstName').toString(), 'prenom', 'left', true),
    )
    this.headers.push(
        new GridHeader(
            this.$t('TEMPACCOUNT.EndDate').toString(),
            'date_validite',
            'left',
            true,
        ),
    )
    this.headers.push(
        new GridHeader(
            this.$t('Description').toString(),
            'description',
            'left',
            true,
        ),
    )
    this.headers.push(
        new GridHeader(
            this.$t('Courriel').toString(),
            'courriel_autre',
            'left',
            true,
        ),
    )
    this.headers.push(
        new GridHeader(
            this.$t('TEMPACCOUNT.Active').toString(),
            'ad_est_actif',
            'left',
            true,
        ),
    )
    this.headers.push(
        new GridHeader(
            this.$t('Actions').toString(),
            'actions',
            'left fixed',
            false,
            '5%',
            'fixed',
        ),
    )
  }

  private handleDialogClose() {
    if (this.filter.length > 0) {
      return this.search(this.filter)
    }
  }

  private create() {
    this.$refs.createDialog.show()
  }

  private update(tempAccount: TempAccount, step: number = 0) {
    this.$refs.updateDialog.show(this.tempAccounts, tempAccount, step)
  }

  private accountChangeStatus(tempAccount: TempAccount) {
    this.$refs.accountChangeStatusDialog.show(tempAccount)
  }

  private remove(tempAccount: TempAccount) {
    this.$refs.deleteDialog.show(this.tempAccounts, tempAccount)
  }

  private updateExpiryDate(tempAccount: TempAccount) {
    (this.$refs.datePickerDialog as any).show(tempAccount)
  }

  private async handlePickerSelection(event: DatePickerActions) {
    this.isPending = true
    try {
      await TempAccountService.extend(
          event.account.ad_personne_id,
          event.picker,
          event.account.identifiant,
      )
      this.$notificationService.success(
          this.$t('TEMPACCOUNT.ValidityDateSuccess').toString(),
      )
    } catch (error: any) {
      this.errorHandler.handle(this, error.response.data.code, true)
    }
    this.handleDialogClose()
  }

  private get hasAccessToBulkImport(): boolean {
    const allowed = [Permissions.BulkImportTempAccounts]
    if (
        (this.user.permissions as []).some((permission) =>
            allowed.includes(permission),
        )
    ) {
      return true
    }
    return false
  }

  public get parseDate() {
    return this.formatDateToDatePicker(this.selectedExpiration, '-')
  }

  public set parseDate(date: any) {
    this.selectedExpiration = date
    this.filterLogical()
  }

  private formatDateToDatePicker(selectedDate: string, splitCharacter: string) {
    let formattedDate: string | null = null
    if (selectedDate) {
      const [year, month, day] = selectedDate.split(splitCharacter)
      formattedDate = `${day}.${month}.${year}`
    }
    return formattedDate
  }

  @Watch('filter')
  private async onFilterChanged(filter: string, oldFilter: string) {
    if (filter === '' || filter === null) {
      this.tempAccounts = []
      this.filteredAccounts = []
      this.clearFiltersSelection()
      this.filter = ''
    } else if (filter.length > 2) {
      // Prevents sending request while typing
      await new Promise((resolve) => setTimeout(resolve, 600))
      if (!this.isPending && this.filter === filter) {
        this.search(filter)
      }
    } else if (filter.length === 0 && this.tempAccounts.length > 0) {
      this.tempAccounts = []
    }
  }

  private get filterSelected() {
    const filters = ['selectedName', 'selectedFirstName', 'selectedUsername', 'selectedExpiration', 'selectedDescription', 'selectedEmail', 'selectedAccountStatus']
    return filters.filter((filter) => this.$data[filter] !== '' && this.$data[filter] !== null)
  }

  private resetFilter(item) {
    this.$data[item] = ''
    this.filterLogical()
  }

  private filterLogical() {
    // this.$emit('update:page', this.page)
    this.clearFilterData()
    this.filteredAccounts = this.tempAccounts.filter((user) => {
      if (this.selectedName && user.nom !== this.selectedName) {
        return false
      }
      if (this.selectedFirstName && user.prenom !== this.selectedFirstName) {
        return false
      }
      if (this.selectedUsername && user.identifiant !== this.selectedUsername) {
        return false
      }
      if (this.selectedExpiration) {
        const dateToCompare = user.date_validite.toLocaleString().split('T')[0]
        if (dateToCompare.indexOf(this.selectedExpiration) === -1) {
          return false
        }
      }
      if (
          this.selectedDescription &&
          user.description !== this.selectedDescription
      ) {
        return false
      }
      if (this.selectedEmail && user.courriel_autre !== this.selectedEmail) {
        return false
      }
      if (this.selectedAccountStatus) {
        if (
            this.selectedAccountStatus === 'Activé' &&
            user.ad_est_actif === false
        ) {
          return false
        } else if (
            this.selectedAccountStatus === 'Désactivé' &&
            user.ad_est_actif === true
        ) {
          return false
        }
      }
      this.populateFilterData(user)
      return true
    })
  }
}
